import { useState } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { isGerman } from '@/i18n/locale';

// sessionStorage survives the full-page redirect to Google and back to
// our /auth/callback page. AuthCallback reads and clears this key.
const AUTH_REDIRECT_KEY = 'alma:auth:redirect_after';

interface Props {
  label?: string;
  onError?: (msg: string) => void;
  // If set, after successful OAuth the user is sent to this path instead
  // of the default /today. Must start with "/" and not "//" (open-redirect
  // guard is enforced again in AuthCallback as a defense-in-depth).
  redirectAfter?: string;
}

// "Continue with Google" — works for both signin and signup. Supabase
// auto-creates the profile on first OAuth signup, then signs in on
// subsequent visits. So one button does both jobs.
export function GoogleSignInButton({ label, onError, redirectAfter }: Props) {
  const de = isGerman();
  const text = label ?? (de ? 'Mit Google fortfahren' : 'Continue with Google');
  const signInWithGoogle = useAuthStore((s) => s.signInWithGoogle);
  const [loading, setLoading] = useState(false);

  const onClick = async () => {
    setLoading(true);

    // Stash the desired post-auth destination BEFORE navigating away.
    // sessionStorage is per-tab and survives the round-trip to Google.
    try {
      if (redirectAfter && isSafeRedirect(redirectAfter)) {
        sessionStorage.setItem(AUTH_REDIRECT_KEY, redirectAfter);
      } else {
        sessionStorage.removeItem(AUTH_REDIRECT_KEY);
      }
    } catch {
      // Some privacy modes block sessionStorage. Continue without resume —
      // user lands on /today, which is acceptable.
    }

    const { error } = await signInWithGoogle();
    // If the OAuth redirect succeeds, the page navigates away and we never
    // re-render. We only land here if the redirect itself fails.
    if (error) {
      setLoading(false);
      onError?.(error);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      aria-busy={loading || undefined}
      className="w-full flex items-center justify-center gap-2.5 rounded-xl bg-white border border-slate/15 px-4 py-3 text-sm font-medium text-slate hover:bg-cream hover:border-slate/25 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {/* Google "G" mark — official multi-color rendering, no external dep */}
      <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
        <path
          fill="#4285F4"
          d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        />
        <path
          fill="#34A853"
          d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        />
        <path
          fill="#FBBC05"
          d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        />
        <path
          fill="#EA4335"
          d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        />
      </svg>
      <span>{loading ? (de ? 'Weiterleitung zu Google…' : 'Redirecting to Google…') : text}</span>
    </button>
  );
}

// Open-redirect guard. Only allow same-origin paths.
//   "/pricing?plan=monthly"   ✓
//   "/today"                  ✓
//   "//evil.com"              ✗ (protocol-relative)
//   "https://evil.com"        ✗ (absolute)
//   "javascript:alert(1)"     ✗
function isSafeRedirect(path: string): boolean {
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  return true;
}

// Re-export for AuthCallback. Kept colocated with the writer so the key
// can't drift between writer and reader.
export { AUTH_REDIRECT_KEY };
